import React, { useState, useEffect } from 'react';
import { ChecklistItem } from '../../../../interfaces/changeRequest';
import '../EditChangeRequest.less';
import { Button, Divider, Form, FormProps, Input } from 'antd';
import { useAgentSelector } from '../../../../hooks/useAgentStore';
import { ChangeRequestSource } from '../../../../enums/changeRequest';
import HeaderWithNavigation from '../HeaderWithNavigation';
import { EditChangeRequestProps } from '../../../../interfaces/changeRequestProps';
import { useChangeRequestEditForm } from '../../../../hooks/useChangeRequestEditForm';
import { ChangeRequestDirection } from '../../../../enums/changeRequest';
import { Notify } from '../../../../services/notificationService';
import ChangeRequestCopilot from '../../../ChangeRequestCopilot/ChangeRequestCopilot';

const EditChangeRequestAnalysis: React.FC<
  EditChangeRequestProps<ChecklistItem>
> = ({
  editingRecord,
  onClose,
  onNavigate,
  onSave,
  itemCount,
  isDirty,
  setIsDirty,
  expandToFullScreen,
}) => {
  const [form] = Form.useForm();
  const [saveStatus, setSaveStatus] = useState<'draft' | 'reviewed' | null>(
    null
  );
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [remountCopilotKey, setRemountCopilotKey] = useState(0);
  const { loading, source } = useAgentSelector((state) => state.changeRequest);

  const _loading =
    source === ChangeRequestSource.EDIT_ANALYSIS_CHECKLIST && loading;

  const { handleClose, handleNavigate, onValuesChange } =
    useChangeRequestEditForm({
      onClose,
      onNavigate,
      isDirty,
      setIsDirty,
    });

  useEffect(() => {
    form.resetFields();
    setIsDirty(false);
    setRemountCopilotKey((prevKey) => prevKey + 1);
  }, [editingRecord]);

  const onSubmit: FormProps<ChecklistItem>['onFinish'] = async (values) => {
    try {
      if (saveStatus) {
        onSave({ ...editingRecord, ...values, status: saveStatus });
        setIsDirty(false);
      }
    } catch (error) {
      Notify.error('Error saving change request analysis');
    }
  };

  const handleExpandToFullScreen = (expand: boolean) => {
    expand && expandToFullScreen();
    setIsFullScreen(expand);
  };

  const updateAnswerField = (answer: string) => {
    form.setFieldsValue({ answer });
    setIsDirty(true);
  };

  /**
   * Navigate to the next or previous checklist item and remount the Copilot component.
   * @param direction - The direction to navigate.
   */
  const handleNavigateWithRemount = (direction: ChangeRequestDirection) => {
    handleNavigate(direction);
    setRemountCopilotKey((prevKey) => prevKey + 1);
  };

  return (
    <>
      {isFullScreen && <div className="custom-modal-overlay"></div>}
      <div
        id="edit-change-request-analysis"
        className={`edit-change-request extra-panel ${isFullScreen ? 'full-screen custom-modal' : ''}`}
      >
        <HeaderWithNavigation
          title={editingRecord.item}
          onClose={handleClose}
          onNavigate={handleNavigateWithRemount}
          itemCount={itemCount}
          expandToFullScreen={handleExpandToFullScreen}
        />
        <div className="edit-change-request-body-container">
          <Form
            form={form}
            onFinish={onSubmit}
            onValuesChange={onValuesChange}
            layout="vertical"
            className="edit-change-request-body"
            initialValues={{ layout: 'vertical', ...editingRecord }}
            data-hs-do-not-collect="true"
          >
            <Form.Item
              label="Item"
              name="item"
              rules={[{ required: true, message: 'Please input item!' }]}
            >
              <Input disabled={_loading} />
            </Form.Item>
            <Form.Item
              label="Question"
              name="question"
              rules={[{ required: true, message: 'Please input question!' }]}
            >
              <Input.TextArea
                autoSize={{ minRows: 2, maxRows: 10 }}
                disabled={_loading}
              />
            </Form.Item>
            <Form.Item
              label="Answer"
              name="answer"
              rules={[
                {
                  required: saveStatus === 'reviewed',
                  message: 'Please input answer!',
                },
              ]}
            >
              <Input.TextArea
                autoSize={{ minRows: 2, maxRows: 10 }}
                disabled={_loading}
              />
            </Form.Item>
            <Form.Item className="form-actions">
              <Button
                htmlType="submit"
                onClick={() => setSaveStatus('draft')}
                disabled={_loading}
              >
                Save as Draft
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                onClick={() => setSaveStatus('reviewed')}
                disabled={_loading}
              >
                Mark as Reviewed
              </Button>
            </Form.Item>
          </Form>
          <div className="divider-container">
            <Divider />
          </div>

          <footer className="edit-change-request-footer">
            <ChangeRequestCopilot
              uniqueKey={remountCopilotKey}
              editingRecord={editingRecord}
              onUpdateAnswer={updateAnswerField}
            />
          </footer>
        </div>
      </div>
    </>
  );
};

export default EditChangeRequestAnalysis;
